// Placements panel — shared by candidate-detail (candidateId) and job-order-detail (jobOrderId).
// Writes are role-gated server-side; we mirror that in the UI to avoid confusing 403s.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuthStore } from "@/stores/auth-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { BulkModal } from "@/components/bulk-modal";
import { Plus, Trash2 } from "lucide-react";

const PLACEMENT_STATUSES = ["ACTIVE", "COMPLETED", "TERMINATED"] as const;
const CREATE_ROLES = ["STAFFING_MANAGER", "ADMIN", "SUPER_ADMIN", "RECRUITER"];
const EDIT_ROLES = ["STAFFING_MANAGER", "ADMIN", "SUPER_ADMIN"];
const DELETE_ROLES = ["ADMIN", "SUPER_ADMIN"];

const apiErr = (e: any) => e?.response?.data?.message ?? e?.response?.data?.error?.message ?? e?.message ?? "Something went wrong";
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : "—");
const money = (v: any) => (v == null ? null : `$${Number(v).toLocaleString()}`);

interface Props {
  candidateId?: string;
  jobOrderId?: string;
}

export function StaffingPlacementsPanel({ candidateId, jobOrderId }: Props) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.me);
  const canCreate = !!me && CREATE_ROLES.includes(me.role);
  const canEdit = !!me && EDIT_ROLES.includes(me.role);
  const canDelete = !!me && DELETE_ROLES.includes(me.role);
  const mode = candidateId ? "candidate" : "jobOrder";
  const params = candidateId ? { candidateId } : { jobOrderId };
  const queryKey = ["/placements", params];
  const [addOpen, setAddOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => (await api.get("/placements", { params })).data.data as any[],
  });
  const rows = data ?? [];
  const showMoney = rows.some((r: any) => r.billRate != null || r.payRate != null || r.marginUsd != null);

  async function setStatus(id: string, status: string) {
    setErr(null);
    try {
      await api.put(`/placements/${id}`, { status });
      qc.invalidateQueries({ queryKey });
    } catch (e) {
      setErr(apiErr(e));
    }
  }
  async function remove(id: string) {
    if (!confirm("Delete this placement?")) return;
    setErr(null);
    try {
      await api.delete(`/placements/${id}`);
      qc.invalidateQueries({ queryKey });
    } catch (e) {
      setErr(apiErr(e));
    }
  }

  return (
    <div className="space-y-3">
      {err && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{err}</p>}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{rows.length} placement{rows.length === 1 ? "" : "s"}</div>
        {canCreate && <Button size="sm" onClick={() => setAddOpen(true)}><Plus className="mr-1 h-4 w-4" />New placement</Button>}
      </div>
      {isLoading && <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>}
      {!isLoading && rows.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No placements.</p>}
      {rows.length > 0 && (
        <div className="rounded-md border">
          <Table>
            <Thead>
              <Tr>
                <Th>{mode === "candidate" ? "Job Order" : "Candidate"}</Th>
                <Th>Start</Th>
                <Th>End</Th>
                <Th>Status</Th>
                {showMoney && <Th>Bill</Th>}
                {showMoney && <Th>Pay</Th>}
                {showMoney && <Th>Margin</Th>}
                <Th className="w-10"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((r: any) => (
                <Tr key={r.id} className="group">
                  <Td className="font-medium">
                    {mode === "candidate"
                      ? <Link to={`/job-orders/${r.jobOrder?.id}`} className="hover:underline">{r.jobOrder?.title ?? "—"}</Link>
                      : <Link to={`/candidates/${r.candidate?.id}`} className="hover:underline">{r.candidate?.name ?? "—"}</Link>}
                  </Td>
                  <Td className="text-sm">{fmtDate(r.startDate)}</Td>
                  <Td className="text-sm">{fmtDate(r.endDate)}</Td>
                  <Td>
                    {canEdit
                      ? <Select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className="h-7 w-32 text-xs">{PLACEMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</Select>
                      : <Badge variant="secondary">{r.status}</Badge>}
                  </Td>
                  {showMoney && <Td className="text-sm">{money(r.billRate) ?? "—"}</Td>}
                  {showMoney && <Td className="text-sm">{money(r.payRate) ?? "—"}</Td>}
                  {showMoney && <Td className="text-sm">{money(r.marginUsd) ?? "—"}</Td>}
                  <Td>
                    {canDelete && <button onClick={() => remove(r.id)} className="text-destructive opacity-0 group-hover:opacity-100" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      )}
      {addOpen && (
        <AddPlacementModal
          mode={mode}
          candidateId={candidateId}
          jobOrderId={jobOrderId}
          onClose={() => setAddOpen(false)}
          onAdded={() => qc.invalidateQueries({ queryKey })}
        />
      )}
    </div>
  );
}

function AddPlacementModal({ mode, candidateId, jobOrderId, onClose, onAdded }: { mode: string; candidateId?: string; jobOrderId?: string; onClose: () => void; onAdded: () => void }) {
  const [q, setQ] = useState("");
  const [pickId, setPickId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [billRate, setBillRate] = useState("");
  const [payRate, setPayRate] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const pickEntity = mode === "candidate" ? "job-orders" : "candidates";
  const { data, isFetching } = useQuery({
    queryKey: ["picker", pickEntity, q],
    queryFn: async () => (await api.get(`/${pickEntity}`, { params: { q, pageSize: 25 } })).data.data as any[],
  });
  const list = data ?? [];
  const valid = pickId && startDate && billRate !== "" && payRate !== "";
  const mut = useMutation({
    mutationFn: async () => {
      const body: any = mode === "candidate" ? { candidateId, jobOrderId: pickId } : { candidateId: pickId, jobOrderId };
      body.startDate = startDate;
      if (endDate) body.endDate = endDate;
      body.billRate = Number(billRate);
      body.payRate = Number(payRate);
      await api.post("/placements", body);
    },
    onSuccess: () => { onAdded(); onClose(); },
    onError: (e) => setErr(apiErr(e)),
  });
  return (
    <BulkModal open onClose={onClose} title="New placement" confirmLabel="Create" confirmDisabled={!valid} busy={mut.isPending} error={err} onConfirm={() => mut.mutate()}>
      <div className="space-y-1">
        <Label>{mode === "candidate" ? "Job order" : "Candidate"}</Label>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" />
      </div>
      <div className="max-h-40 overflow-auto rounded-md border">
        {isFetching && <p className="p-2 text-sm text-muted-foreground">Loading…</p>}
        {!isFetching && list.length === 0 && <p className="p-2 text-sm text-muted-foreground">No matches.</p>}
        {list.map((it: any) => (
          <label key={it.id} className="flex cursor-pointer items-center gap-2 border-b px-3 py-1.5 text-sm last:border-0 hover:bg-muted">
            <input type="radio" name="pp" checked={pickId === it.id} onChange={() => setPickId(it.id)} />
            <span className="font-medium">{mode === "candidate" ? it.title : it.name}</span>
          </label>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label>Start date</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
        <div className="space-y-1"><Label>End date</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></div>
        <div className="space-y-1"><Label>Bill rate ($/hr)</Label><Input type="number" value={billRate} onChange={(e) => setBillRate(e.target.value)} /></div>
        <div className="space-y-1"><Label>Pay rate ($/hr)</Label><Input type="number" value={payRate} onChange={(e) => setPayRate(e.target.value)} /></div>
      </div>
    </BulkModal>
  );
}
